import { ButtonComponent } from '@/components/commons'
import { Text } from '@/components/commons/typography/Text'
import { Title } from '@/components/commons/typography/Title'
import { Modal } from '@/components/modal/Modal'
import { cssClass } from '@/shared/utils'
import { CheckIcon } from '@mantine/core'
import { useEffect, useMemo, useState } from 'react'
import { DealSyncContactsInvited } from './DealSyncContactsInvited'
import { DealSyncContact } from './types/deal-sync'

interface DealSyncContactModalProps {
  isOpen?: boolean
  onClose: () => void
  onInvite: (contacts: DealSyncContact[]) => void
  contacts: DealSyncContact[]
  isLoading?: boolean
  contactsInvited?: boolean
}

export const DealSyncContactsModal = ({
  isOpen,
  onClose,
  onInvite,
  contacts,
  isLoading,
  contactsInvited,
}: DealSyncContactModalProps) => {
  const [selectedContacts, setSelectedContacts] = useState(contacts)

  useEffect(() => {
    setSelectedContacts(contacts)
  }, [contacts])

  const handleToggleContact = (email: string) => {
    const isSelected = selectedContacts.some(
      (contact) => contact.contactEmail === email
    )
    if (isSelected) {
      setSelectedContacts(
        selectedContacts.filter((contact) => contact.contactEmail !== email)
      )
      return
    }

    const contact = contacts.find((contact) => contact.contactEmail === email)
    setSelectedContacts([...selectedContacts, contact])
  }

  const isContactSelected = (email: string) => {
    return selectedContacts.some((contact) => contact.contactEmail === email)
  }

  const selectAll = () => {
    setSelectedContacts(contacts)
  }

  const clearAll = () => {
    setSelectedContacts([])
  }

  const allContactSelected = useMemo(() => {
    return contacts.length === selectedContacts.length
  }, [contacts, selectedContacts])

  if (contactsInvited) {
    return <DealSyncContactsInvited isOpen={isOpen} onClose={onClose} />
  }

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      // title="🚀 Discover Your VIP Network"
      size="xl"
    >
      <div className="flex flex-column gap-24 gap-36--desktop items-center px-30--tablet">
        <Title variation="regular" sizeClass="text-20--tablet">
          🚀 Discover Your VIP Network
        </Title>
        <Text className="text-center">
          This list includes VIP contacts identified from your Gmail—based on
          past collaborations, partnership offers, and content creation
          activity. Select who you’d like to connect with and grow your
          Creatorland network.
        </Text>
        <div
          style={{
            maxHeight: '400px',
            height: '40vh',
            overflowY: 'scroll',
            overflowX: 'hidden',
            // Add scrollbar width
            paddingRight: '17px',
            // add scroll style
            scrollbarWidth: 'thin',
            scrollbarColor: '#E5E5E5 #F4F4F4',
            // add Scroll bar always visible
          }}
          className="flex justify-center items-center full-w"
        >
          <table
            className="full-w"
            style={{
              maxHeight: '375px',
              height: '40vh',
              display: 'block',
            }}
          >
            <thead style={{ width: '100%', display: 'inline-table' }}>
              <tr className="">
                <th
                  style={{ width: '50%', textAlign: 'left' }}
                  className="border-b border-b-solid border-secondary text-left font-semibold text-14 text-15--tablet"
                >
                  VIP Contact
                </th>
                <th className="border-b border-b-solid border-secondary text-left font-semibold text-14 text-15--tablet">
                  Email Address
                </th>
              </tr>
            </thead>
            <tbody style={{ width: '100%', display: 'inline-table' }}>
              {contacts.map((contact, index) => {
                const isSelected = isContactSelected(contact.contactEmail)
                return (
                  <tr
                    key={index + '_contact_modal_' + contact.contactEmail}
                    className="border-b"
                  >
                    <td style={{ width: '50%', textAlign: 'left' }}>
                      <div className="flex items-center py-3 gap-10">
                        <div
                          style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '100%',
                          }}
                          onClick={() =>
                            handleToggleContact(contact.contactEmail)
                          }
                          className={cssClass([
                            'rounded-full flex items-center justify-center cursor-pointer',
                            isSelected
                              ? 'bg-accent-primary'
                              : 'border border-solid border-1',
                          ])}
                        >
                          {isSelected && (
                            <CheckIcon
                              style={{ width: '8px', height: '8px' }}
                            />
                          )}
                        </div>
                        <span className="text-13 text-14--tablet">
                          {contact.contactName}
                        </span>
                      </div>
                    </td>
                    <td
                      style={{ width: '50%', textAlign: 'left' }}
                      className="py-3 text-13 text-14--tablet"
                    >
                      {contact.contactEmail}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <ButtonComponent
          size="small"
          fullWidth
          label="Invite to connect"
          onClick={() => onInvite(selectedContacts)}
          disabled={selectedContacts.length === 0}
          loading={isLoading}
        />
        <div className="flex items-center justify-center full-w gap-20">
          <ButtonComponent
            size="xSmall"
            label="Clear all"
            onClick={clearAll}
            disabled={selectedContacts.length === 0 || isLoading}
            variation="secondary-no-border"
            customSize={{
              padding: 0,
            }}
          />
          <ButtonComponent
            size="xSmall"
            label="Select all"
            onClick={selectAll}
            disabled={allContactSelected || isLoading}
            variation="secondary-no-border"
            customSize={{
              padding: 0,
            }}
          />
        </div>
      </div>
    </Modal>
  )
}
